import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Box, IconButton, Skeleton, Stack } from '@mui/material'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { TrainingSprintStepProps, Activity, ActivityType, TrainingPurpose } from '../types'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { ButtonSecondary, Input, BackgroundBoxHeritageContainer } from '../../../../interaxDesignSystemComponents'
import { AddActivitySection } from '../components/AddActivitySection/AddActivitySection'
import { ConfigureActivitiesSection } from '../components/ConfigureActivitiesSection/ConfigureActivitiesSection'
import { GameActivityLoopSettings } from '../components/GameActivityLoopSettings/GameActivityLoopSettings'
import { ActivityDurationField } from '../components/ActivityDurationField/ActivityDurationField'
import { shouldShowLoopConfiguration } from '../utils/activityConfiguration'
import { useSprintWizardConfig } from '../hooks/useSprintWizardConfig'
import { useUserPurposeTemplates } from '../../../../apiLayer/queryHooks/useUserPurposeTemplates'
import { hydrateTemplate } from '../utils/templateHydration'
import { PurposeTemplateChipStrip } from '../components/PurposeTemplateChipStrip/PurposeTemplateChipStrip'
import { SaveActivityTemplateDialog } from '../components/SaveActivityTemplateDialog/SaveActivityTemplateDialog'
import type { PurposeTemplate } from '../../../../../../pure-js/types/PurposeTemplate'
import { registerSaveTemplateCallback, unregisterSaveTemplateCallback } from '../utils/saveTemplateRegistry'
import { TESTIDS } from '../../../../config/testids'

const SECTION_GAP = 32

export const TrainingSpringContentStepTwo: React.FC<TrainingSprintStepProps> = ({ data, updateData }) => {
  const { t } = useTranslation()
  const { availableActivities, isLoading: configLoading } = useSprintWizardConfig()
  const [selectedActivities, setSelectedActivities] = useState<Activity[]>(data.activities || [])
  const [sprintName, setSprintName] = useState<string>(data.sprintName || '')
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)
  const [editingDurationId, setEditingDurationId] = useState<string | null>(null)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const templates = useUserPurposeTemplates()

  const isCustomPurpose = data.purpose === TrainingPurpose.CUSTOM

  useEffect(() => {
    setSelectedActivities(data.activities || [])
    setSprintName(data.sprintName || '')
  }, [data.activities, data.sprintName])

  useEffect(
    () => () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    },
    []
  )

  const activityCountByType = useMemo(() => {
    return selectedActivities.reduce<Partial<Record<ActivityType, number>>>((counts, activity) => {
      counts[activity.type] = (counts[activity.type] ?? 0) + 1
      return counts
    }, {})
  }, [selectedActivities])

  const canAddActivity = useCallback(
    (activity: Activity) => {
      if (!activity.limit) return true
      return (activityCountByType[activity.type] ?? 0) < activity.limit
    },
    [activityCountByType]
  )

  const upsertActivities = useCallback(
    (activities: Activity[]) => {
      setSelectedActivities(activities)
      updateData('activities', activities)
    },
    [updateData]
  )

  const handleActivityAdd = useCallback(
    (newActivity: Activity) => {
      if (!canAddActivity(newActivity)) {
        return
      }

      const isFixed =
        !isCustomPurpose &&
        (newActivity.type === ActivityType.INTRODUCTION || newActivity.type === ActivityType.FIRST_GAME)

      const nextActivity: Activity = { ...newActivity, isFixed }

      const nextActivities = [...selectedActivities, nextActivity]

      if (nextActivity.type === ActivityType.INTRODUCTION) {
        const idx = nextActivities.findIndex((item) => item.id === nextActivity.id)
        const [introduction] = nextActivities.splice(idx, 1)
        nextActivities.unshift(introduction)
      } else if (nextActivity.type === ActivityType.FIRST_GAME) {
        const idx = nextActivities.findIndex((item) => item.id === nextActivity.id)
        const firstGame = nextActivities.splice(idx, 1)[0]
        const introductionIndex = nextActivities.findIndex((item) => item.type === ActivityType.INTRODUCTION)
        const insertIndex = introductionIndex >= 0 ? introductionIndex + 1 : 0
        nextActivities.splice(insertIndex, 0, firstGame)
      }

      upsertActivities(nextActivities)
    },
    [canAddActivity, isCustomPurpose, selectedActivities, upsertActivities]
  )

  const handleActivityRemove = useCallback(
    (id: string) => {
      const filtered = selectedActivities.filter((activity) => activity.id !== id)
      upsertActivities(filtered)
    },
    [selectedActivities, upsertActivities]
  )

  const handleDragEnd = useCallback((activities: Activity[]) => upsertActivities(activities), [upsertActivities])

  const handleActivityLoopChange = useCallback(
    (activityId: string, numberOfLoops: number) => {
      const nextActivities = selectedActivities.map((activity) =>
        activity.id === activityId ? { ...activity, numberOfLoops } : activity
      )

      upsertActivities(nextActivities)
    },
    [selectedActivities, upsertActivities]
  )

  const handleActivityDurationChange = useCallback(
    (activityId: string, duration: number) => {
      const nextActivities = selectedActivities.map((activity) =>
        activity.id === activityId ? { ...activity, duration } : activity
      )

      upsertActivities(nextActivities)
    },
    [selectedActivities, upsertActivities]
  )

  const handleSprintNameChange = useCallback(
    (value: string) => {
      setSprintName(value)
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
      debounceTimer.current = setTimeout(() => {
        updateData('sprintName', value)
      }, 300)
    },
    [updateData]
  )

  const handleCustomizeClick = useCallback(() => {
    updateData('purpose', TrainingPurpose.CUSTOM)
  }, [updateData])

  useEffect(() => {
    registerSaveTemplateCallback(() => setSaveTemplateOpen(true))
    return () => unregisterSaveTemplateCallback()
  }, [])

  const handleLoadTemplate = useCallback(
    (template: PurposeTemplate) => {
      const hydrated = hydrateTemplate(template.activities, availableActivities)
      upsertActivities(hydrated)
    },
    [availableActivities, upsertActivities]
  )

  // Check if the sprint name is the default "Untitled Training Sprint"
  const isDefaultName = sprintName.trim() === 'Untitled Training Sprint'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SECTION_GAP}px` }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.typographyText, mb: 1 }}>
          {t('facilitator.trainingSprint.contentStep.title')}
        </Box>
        <Box className="text-body-2" sx={{ color: INTERAX_COLORS.typographyFont, maxWidth: 760, mx: 'auto' }}>
          {t('facilitator.trainingSprint.contentStep.subtitle')}
        </Box>
      </Box>

      <BackgroundBoxHeritageContainer sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box className="text-headline-4" sx={{ color: INTERAX_COLORS.typographyText }}>
          {t('facilitator.trainingSprint.contentStep.nameYourSprint')}
        </Box>
        <Input
          value={sprintName}
          onChange={(event) => handleSprintNameChange(event.target.value)}
          placeholder={t('facilitator.trainingSprint.contentStep.enterName')}
          fullWidth
          size="large"
          error={isDefaultName}
          errorMessage={isDefaultName ? t('facilitator.trainingSprint.validation.sprintNameCustomRequired') : undefined}
          data-testid="sprint-name-input"
        />
      </BackgroundBoxHeritageContainer>

      {isCustomPurpose && templates.length > 0 && (
        <BackgroundBoxHeritageContainer sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PurposeTemplateChipStrip templates={templates} onLoadTemplate={handleLoadTemplate} />
        </BackgroundBoxHeritageContainer>
      )}

      {isCustomPurpose && (
        <BackgroundBoxHeritageContainer sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {configLoading ? (
            <Stack direction="column" spacing={2}>
              <Skeleton variant="rounded" width="100%" height={72} />
              <Skeleton variant="rounded" width="100%" height={72} />
              <Skeleton variant="rounded" width="100%" height={72} />
              <Skeleton variant="rounded" width="100%" height={72} />
              <Skeleton variant="rounded" width="100%" height={72} />
              <Skeleton variant="rounded" width="100%" height={72} />
            </Stack>
          ) : (
            <AddActivitySection
              selectedActivities={selectedActivities}
              onAddActivity={handleActivityAdd}
              availableActivities={availableActivities}
            />
          )}
        </BackgroundBoxHeritageContainer>
      )}

      <BackgroundBoxHeritageContainer sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 3
          }}
        >
          <Box sx={{ maxWidth: '640px' }}>
            <Box className="text-headline-4" sx={{ color: INTERAX_COLORS.typographyText, mb: 0.5 }}>
              {t('facilitator.trainingSprint.contentStep.configureActivities')}
            </Box>
            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.typographyFont }}>
              {isCustomPurpose
                ? t('facilitator.trainingSprint.contentStep.configureActivitiesCustom')
                : t('facilitator.trainingSprint.contentStep.configureActivitiesPreSelected')}
            </Box>
          </Box>

          {!isCustomPurpose && (
            <ButtonSecondary
              className="!px-6 !py-2 !text-button-small"
              onClick={handleCustomizeClick}
              data-testid={TESTIDS.SPRINT_CUSTOMIZE_BTN}
            >
              {t('common.customize') || 'Customize'}
            </ButtonSecondary>
          )}
        </Box>

        {selectedActivities.length === 0 ? (
          <Box className="text-body-2" sx={{ color: INTERAX_COLORS.typographyFont, textAlign: 'center', py: 6 }}>
            {t('facilitator.trainingSprint.contentStep.emptyState')}
          </Box>
        ) : (
          <ConfigureActivitiesSection
            selectedActivities={selectedActivities}
            isCustomPurpose={isCustomPurpose}
            onReorderActivities={handleDragEnd}
            onRemoveActivity={handleActivityRemove}
            renderDurationAction={(activity) =>
              // Games configure loops, and the AI coached team debrief has a fixed duration → no edit.
              shouldShowLoopConfiguration(activity.type) || activity.type === ActivityType.SHORT_REFLECTION ? null : (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    setEditingDurationId((prev) => (prev === activity.id ? null : activity.id))
                  }}
                  aria-label={t('facilitator.activityConfig.editDuration', 'Edit duration')}
                  sx={{ p: 0.25, ml: 0.5, color: INTERAX_COLORS.sun }}
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </IconButton>
              )
            }
            renderActivityDetails={(activity) =>
              shouldShowLoopConfiguration(activity.type) ? (
                <GameActivityLoopSettings
                  activityType={activity.type}
                  numberOfLoops={activity.numberOfLoops}
                  onNumberOfLoopsChange={(numberOfLoops) => handleActivityLoopChange(activity.id, numberOfLoops)}
                  variant="compact"
                />
              ) : editingDurationId === activity.id ? (
                <ActivityDurationField
                  activityType={activity.type}
                  duration={activity.duration}
                  onDurationChange={(duration) => handleActivityDurationChange(activity.id, duration)}
                  onDone={() => setEditingDurationId(null)}
                />
              ) : null
            }
          />
        )}
      </BackgroundBoxHeritageContainer>

      <SaveActivityTemplateDialog
        open={saveTemplateOpen}
        activities={selectedActivities}
        onClose={() => setSaveTemplateOpen(false)}
      />
    </Box>
  )
}
